import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  Search, ChevronDown, ChevronUp, Mail, Download
} from "lucide-react"

type Status = "All" | "Attended" | "Approved" | "Pending" | "Rejected"
type SortKey = "name" | "email" | "status" | "registeredAt"
type SortDir = "asc" | "desc"

interface Attendee {
  id: string
  name: string
  email: string
  status: "Attended" | "Approved" | "Pending" | "Rejected"
  registeredAt: string
  joinedAt?: string
  duration?: string
  device?: string
}

const mockAttendees: Attendee[] = [
  { id: "a1",  name: "Alice Johnson",  email: "alice@co.com",   status: "Attended", registeredAt: "Apr 10",  joinedAt: "10:02 AM", duration: "85 min", device: "Desktop" },
  { id: "a2",  name: "Bob Martinez",   email: "bob@co.com",     status: "Attended", registeredAt: "Apr 11",  joinedAt: "10:05 AM", duration: "72 min", device: "Mobile"  },
  { id: "a3",  name: "Carol White",    email: "carol@co.com",   status: "Approved", registeredAt: "Apr 12"  },
  { id: "a4",  name: "David Lee",      email: "david@co.com",   status: "Pending",  registeredAt: "Apr 14"  },
  { id: "a5",  name: "Eva Kim",        email: "eva@co.com",     status: "Rejected", registeredAt: "Apr 13"  },
  { id: "a6",  name: "Frank Chen",     email: "frank@co.com",   status: "Attended", registeredAt: "Apr 9",   joinedAt: "10:00 AM", duration: "90 min", device: "Desktop" },
  { id: "a7",  name: "Grace Roy",      email: "grace@co.com",   status: "Attended", registeredAt: "Apr 8",   joinedAt: "10:10 AM", duration: "65 min", device: "Tablet"  },
  { id: "a8",  name: "Henry Patel",    email: "henry@co.com",   status: "Pending",  registeredAt: "Apr 15"  },
  { id: "a9",  name: "Isabel Cruz",    email: "isabel@co.com",  status: "Approved", registeredAt: "Apr 7"   },
  { id: "a10", name: "James Park",     email: "james@co.com",   status: "Attended", registeredAt: "Apr 6",   joinedAt: "10:08 AM", duration: "78 min", device: "Desktop" },
  { id: "a11", name: "Karen Wilson",   email: "karen@co.com",   status: "Attended", registeredAt: "Apr 5",   joinedAt: "10:03 AM", duration: "88 min", device: "Mobile"  },
  { id: "a12", name: "Leo Turner",     email: "leo@co.com",     status: "Rejected", registeredAt: "Apr 16"  },
]

const STATUS_STYLE: Record<Attendee["status"], string> = {
  Attended: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400",
  Approved: "bg-blue-100   text-blue-700   dark:bg-blue-900/20   dark:text-blue-400",
  Pending:  "bg-amber-100  text-amber-700  dark:bg-amber-900/20  dark:text-amber-400",
  Rejected: "bg-red-100    text-red-700    dark:bg-red-900/20    dark:text-red-400",
}

export default function AttendeeList() {
  const t = useTranslation()
  const STATUSES: { id: Status; label: string }[] = [
    { id: "All", label: t("webinar.all") },
    { id: "Attended", label: t("webinar.attended") },
    { id: "Approved", label: t("webinar.approved") },
    { id: "Pending", label: t("webinar.pending") },
    { id: "Rejected", label: t("webinar.rejected") },
  ]
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const initialTab = (searchParams.get("tab") as Status | null)
  const [activeTab, setActiveTab] = useState<Status>(
    STATUSES.map(s => s.id).includes(initialTab as Status) ? (initialTab as Status) : "All"
  )
  const [search, setSearch]   = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("name")
  const [sortDir, setSortDir] = useState<SortDir>("asc")

  const filtered = mockAttendees
    .filter(a => activeTab === "All" || a.status === activeTab)
    .filter(a =>
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.email.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const av = a[sortKey] ?? "", bv = b[sortKey] ?? ""
      if (av < bv) return sortDir === "asc" ? -1 : 1
      if (av > bv) return sortDir === "asc" ?  1 : -1
      return 0
    })

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortKey(key); setSortDir("asc") }
  }

  const SortIcon = ({ col }: { col: SortKey }) =>
    col !== sortKey
      ? <ChevronUp className="w-3 h-3 opacity-20" />
      : sortDir === "asc"
        ? <ChevronUp className="w-3 h-3 text-primary" />
        : <ChevronDown className="w-3 h-3 text-primary" />

  const cols: { key: SortKey; label: string }[] = [
    { key: "name",         label: t("webinar.name") },
    { key: "email",        label: t("webinar.email") },
    { key: "status",       label: t("webinar.status") },
    { key: "registeredAt", label: t("webinar.registered") },
  ]

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto bg-background p-4 md:p-6 gap-5">

      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/webinar")} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.webinar")}</button>
          <span>/</span>
          <button onClick={() => navigate(`/webinar/${id}/analytics`)} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.analytics")}</button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-semibold">{t("webinar.registrantsAndAttendees")}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* <button
              onClick={() => navigate(`/webinar/${id}/analytics`)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button> */}
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.registrantsAndAttendees")}</h1>
              <p className="text-xs text-slate-400 mt-0.5">{filtered.length} {t("webinar.recordsFound")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t("webinar.searchNameOrEmail")}
                className="pl-8 pr-3 py-2 text-sm bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-slate-900 dark:text-foreground/80 dark:text-white w-[200px]"
              />
            </div>
            <button className="flex items-center gap-1.5 border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:border-primary transition-colors">
              <Download className="w-3.5 h-3.5" /> {t("webinar.export")}
            </button>
          </div>
        </div>
      </div>

      {/* Tab Filters */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map(s => {
          const count = s.id === "All" ? mockAttendees.length : mockAttendees.filter(a => a.status === s.id).length
          return (
            <button
              key={s.id} onClick={() => setActiveTab(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === s.id
                  ? "bg-primary text-white shadow-sm"
                  : "bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border text-slate-600 dark:text-slate-400 hover:border-primary"
              }`}
            >
              {s.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === s.id ? "bg-card/20" : "bg-slate-100 dark:bg-slate-700"}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Table */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Mail className="w-14 h-14 text-slate-200 dark:text-slate-700" />
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{t("webinar.noRegistrantsFound")}</p>
            <p className="text-xs text-slate-400">{t("webinar.tryChangingFilter")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stroke-100 dark:border-border dark:border-border bg-slate-50 dark:bg-card/80">
                  {cols.map(c => (
                    <th
                      key={c.key} onClick={() => handleSort(c.key)}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-primary transition-colors select-none"
                    >
                      <span className="flex items-center gap-1">{c.label} <SortIcon col={c.key} /></span>
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.joinedAt")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.duration")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("webinar.device")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id} className="border-b border-slate-50 dark:border-border/40 hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                          {a.name[0]}
                        </div>
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{a.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[a.status]}`}>
                        {t("webinar." + a.status.toLowerCase())}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{a.registeredAt}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{a.joinedAt  ?? "–"}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{a.duration  ?? "–"}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{a.device    ?? "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
